import EditNoteIcon from "@mui/icons-material/EditNote";
import type { SxProps, Theme } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import type { PbjPrediction } from "../../../utils/models";
import { ManageManualHoursDialog } from "./ManageManualHoursDialog";

type Props = {
	departmentId: string;
	startDate: string;
	endDate: string;
	prediction: PbjPrediction;
	// Seed for the dialog's "Period start". Passed through untouched — the button has no opinion on
	// what the right default is, only the surface that renders it does.
	defaultPeriodStart?: DateTime;
	// Promotes the trigger to the primary variant. The caller sets this when the dialog holds
	// something the user needs to act on (jobs missing from the calculation), so the button carries
	// the urgency rather than the surface needing a second call-to-action beside the warning.
	emphasized?: boolean;
	// Optional style override for the trigger button — a caller on a non-default surface passes its
	// own treatment (e.g. a dark stage's translucent "ghost" look).
	buttonSx?: SxProps<Theme>;
};

// Owns the open/close state for `ManageManualHoursDialog`; the dialog itself handles list + writes.
// Rendered in the Scoring Breakdown card's header (DAT-608) — next to the HPPD rows these hours
// move — rather than in the hero, where it read as a peer of "Set Target" and implied it changed
// the goal rather than the inputs.
export const ManualHoursButton: FC<Props> = ({ departmentId, startDate, endDate, prediction, defaultPeriodStart, emphasized = false, buttonSx }) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);

	return (
		<>
			<SystemButton
				buttonVariant={emphasized ? "primary" : "secondary"}
				size="small"
				leadingIcon={<EditNoteIcon sx={{ fontSize: 16 }} />}
				onClick={() => setOpen(true)}
				noFullWidthMobile
				sx={buttonSx ? [{ width: { xs: "100%", sm: "100%" } }, ...(Array.isArray(buttonSx) ? buttonSx : [buttonSx])] : undefined}
				data-testid="pbj-model-manual-hours-button"
			>
				{t("pbj_model.prediction.manual_hours.button")}
			</SystemButton>
			<ManageManualHoursDialog
				open={open}
				onClose={() => setOpen(false)}
				departmentId={departmentId}
				startDate={startDate}
				endDate={endDate}
				prediction={prediction}
				defaultPeriodStart={defaultPeriodStart}
			/>
		</>
	);
};
